import { Text, Tooltip } from "@instructure/ui";
import DOMPurify from "dompurify";
import { t } from "i18next";
import { sortByLastName } from "../../../../components/Assessment/Rater/utils";
import { TruncateToolTip } from "../../../../utils/TruncateTooltip";
import { sort } from "../../../../utils/utils";

const domPurifyConfig = { ALLOWED_TAGS: ["#text"], KEEP_CONTENT: true };

export const RESULTS_FILE_TYPE = "results";

export const ASSIGNMENT = "assignment";
export const LMG = "lmg";
export const UPLOAD = "upload";

export function enrichResults(sectionId, termId) {
  return { ddl_section_id: sectionId, canvas_enrollment_term_id: termId };
}

export function getFinalizeSteps(t) {
  return [
    t("finalizeCourse.methodOfFinalization"),
    t("reflections.reflections"),
    t("actions.actions"),
    t("finalizeCourse.reviewAndFinalize"),
  ];
}

export function buildLMGTable(results, roundingMethod) {
  let roundingString = "";
  if (roundingMethod === "roundHalfUp") {
    roundingString = "_round_half_up";
  } else if (roundingMethod === "roundDown") {
    roundingString = "_round_down";
  }
  const outcomeColumns = [
    { key: "student", name: "Students", width: "200px", frozen: true },
  ];
  const outcomeList = [];
  const studentRows = [];
  const staleOutcomes = [];

  // Pre-compute staleness per outcome ID across all scores
  const outcomeStaleMap = {};
  results.data.forEach((result) => {
    result.scores.forEach((score) => {
      const id = score.links.outcome;
      outcomeStaleMap[id] =
        outcomeStaleMap[id] ||
        score._staleness === "past_version" ||
        score._staleness === "past_variant";
    });
  });

  results.data.forEach((result) => {
    const studentName = result.links.user_metadata.name;
    const studentRow = {
      name: studentName,
      student: (
        <TruncateToolTip renderTip={studentName}>{studentName}</TruncateToolTip>
      ),
    };

    result.scores.forEach((score) => {
      const outcomeId = score.links.outcome;
      const outcomeName = score.links.outcome_metadata.title;
      const outcomeDescription = DOMPurify.sanitize(
        score.links.outcome_metadata.description,
        domPurifyConfig
      ).trim();

      const originalScore = score.score_metadata.score;
      const roundHalfUpScore = score.score_metadata.round_half_up;
      const roundDownScore = score.score_metadata.round_down;

      if (!outcomeList.find((col) => col.key === outcomeId + roundingString)) {
        if (
          outcomeStaleMap[outcomeId] &&
          !staleOutcomes.find((o) => o.name === outcomeName)
        ) {
          staleOutcomes.push({
            name: outcomeName,
            description: outcomeDescription,
          });
        }
        outcomeList.push({
          key: outcomeId + roundingString,
          label: outcomeName,
          name: (
            <Tooltip
              renderTip={
                <div style={{ maxWidth: 250 }}>
                  <Text size="small">
                    <Text weight="bold" size="small">
                      {outcomeName}:{" "}
                    </Text>
                    {outcomeDescription}
                  </Text>
                </div>
              }
              color="primary-inverse"
            >
              <span>{outcomeName}</span>
            </Tooltip>
          ),
          width: "175px",
          resizable: true,
        });
      }
      studentRow[outcomeId] = (
        <Text
          weight="bold"
          color={
            originalScore >= score.links.outcome_metadata.mastery_points
              ? "success"
              : "danger"
          }
        >
          {`${originalScore} / ${score.links.outcome_metadata.points_possible}`}
        </Text>
      );
      studentRow[outcomeId + "_round_half_up"] = (
        <Text
          weight="bold"
          color={
            roundHalfUpScore >= score.links.outcome_metadata.mastery_points
              ? "success"
              : "danger"
          }
        >
          {`${roundHalfUpScore} / ${score.links.outcome_metadata.points_possible}`}
          <Text as="div" size="x-small" color="secondary">
            {t("finalizeCourse.originalScore")}: {originalScore}
          </Text>
        </Text>
      );
      studentRow[outcomeId + "_round_down"] = (
        <Text
          weight="bold"
          color={
            roundDownScore >= score.links.outcome_metadata.mastery_points
              ? "success"
              : "danger"
          }
        >
          {`${roundDownScore} / ${score.links.outcome_metadata.points_possible}`}
          <Text as="div" size="x-small" color="secondary">
            {t("finalizeCourse.originalScore")}: {originalScore}
          </Text>
        </Text>
      );
    });
    studentRows.push(studentRow);
  });

  const sortedOutcomeList = sort(outcomeList, (outcome) => outcome.label);
  studentRows.sort(sortByLastName);
  return {
    outcomeColumns: [...outcomeColumns, ...sortedOutcomeList],
    studentRows,
    staleOutcomes,
  };
}

export function buildAssignmentTable(results) {
  const outcomeColumns = [
    { key: "student", name: "Students", width: "200px", frozen: true },
  ];
  const outcomeList = [];
  const studentRows = [];
  const staleOutcomes = [];

  // Pre-compute staleness per outcome ID across all results
  const outcomeStaleMap = {};
  results.data.forEach((result) => {
    const id = result.links.learning_outcome;
    outcomeStaleMap[id] =
      outcomeStaleMap[id] ||
      result._staleness === "past_version" ||
      result._staleness === "past_variant";
  });

  results.data.forEach((result) => {
    const outcomeId = result.links.learning_outcome;
    const outcomeName = result.outcome_title;
    const outcomeDescription = DOMPurify.sanitize(
      result.outcome_description,
      domPurifyConfig
    ).trim();
    const studentName = result.user_name;
    const userId = result.links.user;

    if (!outcomeList.find((col) => col.key === outcomeId)) {
      if (
        outcomeStaleMap[outcomeId] &&
        !staleOutcomes.find((o) => o.name === outcomeName)
      ) {
        staleOutcomes.push({
          name: outcomeName,
          description: outcomeDescription,
        });
      }
      outcomeList.push({
        key: outcomeId,
        label: outcomeName,
        name: (
          <Tooltip
            renderTip={
              <div style={{ maxWidth: 250 }}>
                <Text size="small">
                  <Text weight="bold" size="small">
                    {outcomeName}:{" "}
                  </Text>
                  {outcomeDescription}
                </Text>
              </div>
            }
            color="primary-inverse"
          >
            <span>{outcomeName}</span>
          </Tooltip>
        ),
        width: "175px",
        resizable: true,
      });
    }
    const scoreCell = (
      <Text weight="bold" color={result.mastery ? "success" : "danger"}>
        {`${result.score} / ${result.possible}`}
      </Text>
    );
    let studentRow = studentRows.find((row) => row.canvasId === userId);
    if (!studentRow) {
      studentRow = {
        canvasId: userId,
        name: studentName,
        student: (
          <TruncateToolTip renderTip={studentName}>
            {studentName}
          </TruncateToolTip>
        ),
        [outcomeId]: scoreCell,
      };
      studentRows.push(studentRow);
    } else {
      studentRow[outcomeId] = scoreCell;
    }
  });

  const sortedOutcomeList = sort(outcomeList, (outcome) => outcome.label);
  studentRows.sort(sortByLastName);
  return {
    outcomeColumns: [...outcomeColumns, ...sortedOutcomeList],
    studentRows,
    staleOutcomes,
  };
}
